import { useCallback } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { adminApi } from '@/lib/api';
import i18n from '@/i18n';

const ADMIN_API_URL = adminApi();

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  total?: number;
  page?: number;
  limit?: number;
  temp_password?: string;
  company_api_key?: string;
  warning?: string;
}

export interface Company {
  id: string;
  name: string;
  is_active: boolean;
  external_api_id: string | null;
  company_api_key?: string | null;
  is_synced?: boolean;
  has_api_key?: boolean;
  api_environment: 'prod' | 'beta';
  created_at: string;
  updated_at: string;
  users_count?: number;
}

export interface User {
  id: string;
  email: string;
  role: 'super_admin' | 'company_admin' | 'user';
  company_id: string | null;
  company_name: string | null;
  is_active: boolean;
  must_change_password: boolean;
  has_active_session: boolean;
  created_at: string;
  updated_at: string;
}

export interface DashboardStats {
  companies_count: number;
  users_count: number;
  today_usage: number;
  last_week_usage: number;
  recent_activity: Array<{
    id: string;
    usage_type: string;
    created_at: string;
    company_name: string;
    metadata: Record<string, unknown>;
  }>;
}

export interface UsageStats {
  total: number;
  by_company: Array<{
    company_id: string;
    name: string;
    linkedin: number;
    messages: number;
    audio: number;
    total: number;
  }>;
  chart_data: Array<{
    date: string;
    linkedin: number;
    messages: number;
    audio: number;
  }>;
}

export function useAdminApi() {
  const { getToken } = useAuthContext();

  const callApi = useCallback(async <T>(
    action: string,
    params: Record<string, unknown> = {}
  ): Promise<ApiResponse<T>> => {
    const token = getToken();
    
    if (!token) {
      return { success: false, error: i18n.t('noPermission', { ns: 'errors' }) };
    }

    try {
      const response = await fetch(ADMIN_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ action, token, ...params }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Admin API error:', error);
      return { success: false, error: i18n.t('auth.connectionError', { ns: 'errors' }) };
    }
  }, [getToken]);

  // Dashboard
  const getDashboardStats = useCallback(() => 
    callApi<DashboardStats>('get_dashboard_stats'), [callApi]);

  // Companies
  const listCompanies = useCallback((params: { page?: number; limit?: number; search?: string } = {}) =>
    callApi<Company[]>('list_companies', params), [callApi]);

  const createCompany = useCallback((name: string) =>
    callApi<Company>('create_company', { name }), [callApi]);

  const updateCompany = useCallback((company_id: string, data: { name?: string; is_active?: boolean; api_environment?: 'prod' | 'beta' }) =>
    callApi<Company>('update_company', { company_id, ...data }), [callApi]);

  const deleteCompany = useCallback((company_id: string) =>
    callApi('delete_company', { company_id }), [callApi]);

  const syncCompany = useCallback((company_id: string) =>
    callApi<Company>('sync_company', { company_id }), [callApi]);

  const regenerateCompanyApiKey = useCallback((company_id: string) =>
    callApi<Company>('regenerate_company_api_key', { company_id }), [callApi]);

  // Users
  const listUsers = useCallback((params: { page?: number; limit?: number; search?: string; company_id?: string } = {}) =>
    callApi<User[]>('list_users', params), [callApi]);

  const createUser = useCallback((data: { email: string; role: string; company_id?: string }) =>
    callApi<User>('create_user', data), [callApi]);

  const updateUser = useCallback((user_id: string, data: { email?: string; role?: string; company_id?: string; is_active?: boolean }) =>
    callApi('update_user', { user_id, ...data }), [callApi]);

  const resetUserPassword = useCallback((user_id: string) =>
    callApi<{ temp_password: string }>('reset_user_password', { user_id }), [callApi]);

  const deleteUser = useCallback((user_id: string) =>
    callApi('delete_user', { user_id }), [callApi]);

  // Stats
  const getUsageStats = useCallback((params: { company_id?: string; start_date?: string; end_date?: string } = {}) =>
    callApi<UsageStats>('get_usage_stats', params), [callApi]);

  return {
    getDashboardStats,
    listCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    syncCompany,
    regenerateCompanyApiKey,
    listUsers,
    createUser,
    updateUser,
    resetUserPassword,
    deleteUser,
    getUsageStats,
  };
}
